/* eslint-disable  react/no-multi-comp */
/*  eslint-disable react/display-name */
/*  eslint-disable react/prop-types */

import React from 'react';

import { Button, Tag, Tooltip } from '@app/ui';

import * as format from '../utils/formatters';
import shortenStr from '../utils/shortenStr';
import abbreviateAccount from '../utils/abbreviateAccount';
import getCurrencySymbol from '../utils/getCurrencySymbol';
import { getYAxisMap } from '../constants/insights';

const getFooter =
    ({ total, currency }) =>
    (key, type = 'decimal') => {
        if (!total?.[key]) {
            return <div />;
        }
        let value = null;
        switch (type) {
            case 'decimal':
                value = format.decimal(total[key]);
                break;
            case 'sec':
                value = format.decimal(total[key]);
                break;
            case 'currency':
                value = format.currency(total[key], { currency });
                break;
            case 'percentage':
                value = format.percentage(total[key]);
                break;
            default:
                value = total[key];
        }
        return <div>{value}</div>;
    };

export const buildHeaders = ({
    currency,
    total,
    relevantPostsFn,
    relevantBroadcastsFn,
    isAllowedPostSMPBelow30,
    showBroadcastData,
    t,
}) => {
    const footer = getFooter({
        total,
        currency,
    });
    const currencyHeaderSuffix = currency
        ? ` (${currency} ${getCurrencySymbol(currency)})`
        : '';
    let header = [
        {
            Header: t('dataExplorer.columns.exposures'),
            id: 'exposures',
            accessor: 'exposures',
            width: 90,
            Cell: row => <div>{format.decimal(row.value)}</div>,
            format: format.decimal,
            csvFormat: format.rawNumber,
            Footer: footer('exposures'),
        },
        {
            Header: t('dataExplorer.columns.duration'),
            id: 'duration',
            accessor: 'duration',
            width: 90,
            format: format.decimal,
            Cell: row => <div>{format.decimal(row.value)}</div>,
            Footer: footer('duration'),
        },
        {
            Header: t('dataExplorer.columns.impressions'),
            id: 'impressions',
            accessor: 'impressions',
            width: 125,
            Cell: row => {
                const value = format.decimal(row.value);
                return (
                    <div>
                        <span title={value}>{value}</span>
                    </div>
                );
            },
            format: format.decimal,
            csvFormat: format.rawNumber,
            Footer: footer('impressions'),
        },
        {
            Header: t('dataExplorer.columns.videoViews'),
            id: 'videoViews',
            accessor: 'videoViews',
            width: 125,
            Cell: row => {
                const value = format.decimal(row.value);
                return (
                    <div>
                        <span title={value}>{value}</span>
                    </div>
                );
            },
            format: format.decimal,
            csvFormat: format.rawNumber,
            Footer: footer('videoViews'),
        },
        {
            Header: t('dataExplorer.columns.engagements'),
            id: 'engagements',
            accessor: 'engagements',
            width: 125,
            Cell: row => <div>{format.decimal(row.value)}</div>,
            format: format.decimal,
            csvFormat: format.rawNumber,
            Footer: footer('engagements'),
        },
        {
            Header: `${t('dataExplorer.columns.fullMediaValue')}${currencyHeaderSuffix}`,
            id: 'fullMediaValue',
            accessor: 'fullMediaValue',
            width: 125,
            Cell: row => <div>{format.currency(row.value, { currency })}</div>,
            format: value => format.currency(value, { currency }),
            csvFormat: format.rawNumber,
            currency,
            Footer: footer('fullMediaValue', 'currency'),
        },
        {
            Header: t('dataExplorer.columns.averageMvp'),
            id: 'averageMvp',
            accessor: 'averageMvp',
            width: 90,
            Cell: row => <div>{format.percentage(row.value)}</div>,
            format: format.percentage,
            csvFormat: value => format.percentage(value),
            Footer: footer('averageMvp', 'percentage'),
        },
        {
            Header: `${t('dataExplorer.columns.sponsorMediaValue')}${currencyHeaderSuffix}`,
            id: 'sponsorMediaValue',
            accessor: 'sponsorMediaValue',
            width: 125,
            Cell: row => <div>{format.currency(row.value, { currency })}</div>,
            format: value => format.currency(value, { currency }),
            csvFormat: format.rawNumber,
            currency,
            Footer: footer('sponsorMediaValue', 'currency'),
        },
        {
            Header: t('dataExplorer.columns.postCount'),
            id: 'postCount',
            accessor: 'postCount',
            sortable: true,
            width: 90,
            Cell: row => (
                <div style={{ cursor: 'default' }}>
                    {format.decimal(row.value)}
                </div>
            ),
            format: format.decimal,
            style: {
                overflow: 'visible',
            },
            Footer: footer('postCount'),
        },
        {
            Header: t('dataExplorer.columns.broadcastCount'),
            id: 'broadcastCount',
            accessor: 'broadcastCount',
            width: 90,
            Cell: row => {
                const value = format.decimal(row.value);
                return (
                    <div>
                        <span title={value}>{value}</span>
                    </div>
                );
            },
            format: format.decimal,
            Footer: footer('broadcastCount'),
        },
        {
            id: 'redirect',
            width: 40,
            Header: '',
            accessor: '',
            sortable: false,
            Cell: function displayMenu({ row }) {
                const _showPost = () => relevantPostsFn(row);
                const _showBroadcast = () => relevantBroadcastsFn(row);
                const { _original: data } = row;
                const {
                    averageMvp,
                    postCount,
                    broadcastCount,
                    inVenueGameCount,
                } = data;
                const hasPosts = postCount > 0;
                const canShowPost =
                    isAllowedPostSMPBelow30 || averageMvp >= 0.3;

                const isPostDisabled = !(hasPosts && canShowPost);
                const isBroadcastDisabled = !(
                    broadcastCount > 0 && showBroadcastData
                );
                const isInVenueDataUnavailable = !(inVenueGameCount > 0);

                const showDisabledButton =
                    isPostDisabled &&
                    isBroadcastDisabled &&
                    isInVenueDataUnavailable;
                const broadcastClass = !isPostDisabled ? '-m-l-2' : null;
                return (
                    <>
                        {showDisabledButton && (
                            <Tooltip
                                title={t(
                                    'dataExplorer.tooltips.noViewableData',
                                )}
                                placement="bottom-left"
                            >
                                <Button
                                    color="blue"
                                    size="small"
                                    variant="solid"
                                    style={{ width: '30px' }}
                                    disabled
                                >
                                    <i className="fas fa-info" />
                                </Button>
                            </Tooltip>
                        )}
                        {!isPostDisabled && (
                            <Button
                                variant="primary"
                                size="small"
                                style={{ width: '30px' }}
                                onClick={_showPost}
                                data-test="view-posts"
                            >
                                <i className="far fa-images" />
                            </Button>
                        )}
                        {!isBroadcastDisabled && (
                            <Tooltip
                                title={t(
                                    'dataExplorer.tooltips.viewBroadcastFrames',
                                )}
                                placement="bottom-left"
                            >
                                <Button
                                    variant="primary"
                                    size="small"
                                    style={{ width: '30px' }}
                                    className={broadcastClass}
                                    onClick={_showBroadcast}
                                    data-test="view-broadcasts"
                                >
                                    <i className="far fa-tv-retro" />
                                </Button>
                            </Tooltip>
                        )}
                    </>
                );
            },
            style: {
                overflow: 'visible',
            },
        },
    ];

    return header;
};

export const buildCompareHeaders = ({
    currency,
    total,
    metric = 'sponsorMediaValue',
    dateRange,
    compareDateRange,
    t,
}) => {
    const footer = getFooter({
        total,
        currency,
    });
    const yAxisMap = getYAxisMap({
        currency,
        t,
    });
    const { format: metricFormat, type } = yAxisMap[metric];
    let header = [
        {
            Header: dateRange,
            accessor: 'dateRange',
            Cell: row => <div>{metricFormat(row.value)}</div>,
            Footer: footer('dateRange', type),
            format: metricFormat,
            csvFormat: format.rawNumber,
            currency,
        },
        {
            Header: compareDateRange,
            accessor: 'compareDateRange',
            Cell: row => <div>{metricFormat(row.value)}</div>,
            Footer: footer('compareDateRange', type),
            format: metricFormat,
            csvFormat: format.rawNumber,
            currency,
        },
        {
            Header: t('dataExplorer.columns.difference'),
            accessor: 'difference',
            Cell: row => {
                const isPositive = row.value >= 0;
                const className = isPositive
                    ? '-color-tx-suc'
                    : '-color-tx-dan';

                return (
                    <div className={className}>{`${
                        isPositive ? '+' : ''
                    }${metricFormat(row.value)}`}</div>
                );
            },
            Footer: footer('difference', type),
            format: metricFormat,
            csvFormat: format.rawNumber,
            currency,
        },
        {
            Header: t('dataExplorer.columns.percentageChange'),
            accessor: 'percentageChange',
            Cell: row => {
                const className =
                    row.value !== 'N/A'
                        ? row.value >= 0
                            ? '-color-tx-suc'
                            : '-color-tx-dan'
                        : null;
                return (
                    <div className={className}>
                        {row.value !== 'N/A'
                            ? format.percentage(row.value, {}, true)
                            : 'N/A'}
                    </div>
                );
            },
            format: format.percentage,
            Footer: footer('percentageChange', 'percentage'),
            sortMethod: (a, b) => {
                if (Number.isNaN(parseFloat(a))) {
                    return -1;
                }
                if (Number.isNaN(parseFloat(b))) {
                    return 1;
                }
                return parseFloat(a) - parseFloat(b);
            },
            csvFormat: value =>
                value !== 'N/A' ? format.percentage(value, {}, true) : 'N/A',
        },
    ];

    return header;
};

export const getGroupByTable = t => ({
    date: {
        Header: t('groupByOptions.date'),
        accessor: 'date',
        Cell: row => <div>{format.shortDate(row.value)}</div>,
        format: date => format.shortDate(date),
    },
    account: {
        Header: t('groupByOptions.account'),
        accessor: 'accountUsername',
        Cell: row => {
            return (
                <div>
                    {abbreviateAccount(
                        row.value,
                        row.row._original.socialPlatformName,
                    )}
                </div>
            );
        },
        textAlign: 'left',
    },
    mediaUploadName: {
        Header: t('groupByOptions.mediaUploadName'),
        accessor: 'mediaUploadName',
        textAlign: 'left',
    },
    gameName: {
        Header: t('groupByOptions.gameName'),
        accessor: 'gameName',
        textAlign: 'left',
    },
    rightsholder: {
        Header: t('groupByOptions.rightsholder'),
        accessor: 'rightsholderName',
        textAlign: 'left',
    },
    sponsor: {
        Header: t('groupByOptions.sponsor'),
        accessor: 'sponsorName',
        textAlign: 'left',
    },
    rightsholderPlacement: {
        Header: t('groupByOptions.rightsholderPlacement'),
        accessor: 'rightsholderPlacementName',
        textAlign: 'left',
    },
    accountOwnershipType: {
        Header: t('groupByOptions.accountOwnershipType'),
        accessor: 'accountOwnershipTypeName',
        textAlign: 'left',
    },
    socialPlatform: {
        Header: t('groupByOptions.socialPlatform'),
        accessor: 'socialPlatformName',
        textAlign: 'left',
    },
    postType: {
        Header: t('groupByOptions.postType'),
        accessor: 'postType',
        textAlign: 'left',
    },
    mediaType: {
        Header: t('groupByOptions.mediaType'),
        accessor: 'mediaTypeName',
        textAlign: 'left',
    },
    collection: {
        Header: t('groupByOptions.collection'),
        accessor: 'collectionName',
        textAlign: 'left',
    },
    placementType: {
        Header: t('groupByOptions.placementType'),
        accessor: 'placementTypeName',
        textAlign: 'left',
    },
    socialAccountGroup: {
        Header: t('groupByOptions.socialAccountGroup'),
        accessor: 'socialAccountGroupName',
        Cell: row => {
            const socialAccountGroupName =
                row.value || t('groupByOptions.socialAccountGroupUndefined');
            return <div>{socialAccountGroupName}</div>;
        },
        textAlign: 'left',
    },
    country: {
        Header: t('groupByOptions.country'),
        accessor: 'countryName',
        textAlign: 'left',
    },
    seasonType: {
        Header: t('groupByOptions.seasonType'),
        accessor: 'seasonTypeName',
        textAlign: 'left',
    },
    gameType: {
        Header: t('groupByOptions.gameType'),
        accessor: 'gameTypeName',
        textAlign: 'left',
    },
    homeTeam: {
        Header: t('groupByOptions.homeTeam'),
        accessor: 'homeTeamName',
        textAlign: 'left',
    },
    awayTeam: {
        Header: t('groupByOptions.awayTeam'),
        accessor: 'awayTeamName',
        textAlign: 'left',
    },
    authorshipType: {
        Header: t('groupByOptions.authorshipType'),
        accessor: 'authorshipTypeName',
        textAlign: 'left',
        format: value => {
            const display =
                value && String(value).trim()
                    ? value
                    : t('filters.authorshipType.unknown');
            return (
                <Tooltip title={display} placement="top">
                    <Tag
                        style={{
                            fontSize: 12,
                            marginBottom: 2,
                            maxWidth: 120,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {shortenStr(20, display)}
                    </Tag>
                </Tooltip>
            );
        },
        csvFormat: value =>
            value && String(value).trim()
                ? value
                : t('filters.authorshipType.unknown'),
    },
    collaboratingAccounts: {
        Header: t('groupByOptions.collaboratingAccounts'),
        accessor: 'collaboratingAccounts',
        textAlign: 'left',
        format: value => {
            const list =
                value == null
                    ? []
                    : Array.isArray(value)
                      ? value.filter(Boolean)
                      : String(value)
                            .split(',')
                            .map(s => s.trim())
                            .filter(Boolean);
            if (list.length === 0) return null;
            return (
                <>
                    {list.map((username, idx) => {
                        const shortName = shortenStr(20, username);
                        return (
                            <React.Fragment key={`${username}-${idx}`}>
                                <Tooltip title={username} placement="top">
                                    <Tag
                                        style={{
                                            fontSize: 12,
                                            marginBottom: 2,
                                            maxWidth: 120,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {shortName}
                                    </Tag>
                                </Tooltip>
                                <br />
                            </React.Fragment>
                        );
                    })}
                </>
            );
        },
        csvFormat: value => {
            const list =
                value == null
                    ? []
                    : Array.isArray(value)
                      ? value.filter(Boolean)
                      : String(value)
                            .split(',')
                            .map(s => s.trim())
                            .filter(Boolean);
            return list.join(', ');
        },
    },
});
